import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

const FamilyContext = createContext({});

export const useFamily = () => useContext(FamilyContext);

export const FamilyProvider = ({ children }) => {
  const { user } = useAuth();
  const [families, setFamilies] = useState([]);
  const [currentFamily, setCurrentFamily] = useState(null);
  const [loading, setLoading] = useState(true);
  const currentFamilyRef = useRef(null);

  // Keep ref in sync with state
  useEffect(() => {
    currentFamilyRef.current = currentFamily;
  }, [currentFamily]);

  const fetchFamilies = useCallback(async () => {
    try {
      const response = await api.get('/families/');
      const familiesList = response.data.results || response.data;
      setFamilies(familiesList);
      
      // Only validate/update currentFamily if we successfully fetched families
      if (Array.isArray(familiesList)) {
        // Use ref to get current value without causing re-renders
        const currentFamilyValue = currentFamilyRef.current;
        
        // Check if currentFamily is still valid
        if (currentFamilyValue) {
          const currentFamilyInList = familiesList.find(f => f.id === currentFamilyValue.id);
          if (!currentFamilyInList) {
            setCurrentFamily(null);
            localStorage.removeItem('currentFamilyId');
          } else {
            // Only update if the data actually changed (avoid unnecessary updates)
            if (currentFamilyInList.id !== currentFamilyValue.id || 
                JSON.stringify(currentFamilyInList) !== JSON.stringify(currentFamilyValue)) {
              setCurrentFamily(currentFamilyInList);
            }
          }
        }
        
        // Set first family as current if none selected
        if (familiesList.length > 0 && !currentFamilyValue) {
          const savedFamilyId = localStorage.getItem('currentFamilyId');
          const familyToSelect = savedFamilyId
            ? familiesList.find(f => f.id === parseInt(savedFamilyId))
            : familiesList[0];
          if (familyToSelect) {
            setCurrentFamily(familyToSelect);
            localStorage.setItem('currentFamilyId', familyToSelect.id);
          } else if (savedFamilyId) {
            // Saved family ID doesn't exist, clear it
            localStorage.removeItem('currentFamilyId');
            // Use first available family
            if (familiesList.length > 0) {
              setCurrentFamily(familiesList[0]);
              localStorage.setItem('currentFamilyId', familiesList[0].id);
            }
          }
        }
      }
      setLoading(false);
    } catch (error) {
      
      // Don't clear currentFamily on error - it might still be valid
      // Only clear if we get a 401/403 (authentication error)
      if (error.response?.status === 401 || error.response?.status === 403) {
        setCurrentFamily(null);
        localStorage.removeItem('currentFamilyId');
      }
      
      setLoading(false);
    }
  }, []); // Remove currentFamily from dependencies to prevent infinite loop

  useEffect(() => {
    // Fetch families on mount and when user logs in
    if (user) {
      fetchFamilies();
    } else {
      // If user logs out, clear families
      setFamilies([]);
      setCurrentFamily(null);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]); // Fetch when user changes (login/logout)

  const selectFamily = (family) => {
    setCurrentFamily(family);
    if (family) {
      localStorage.setItem('currentFamilyId', family.id);
    }
  };

  const createFamily = async (familyData) => {
    try {
      const response = await api.post('/families/', familyData);
      const newFamily = response.data;
      
      // Refresh the families list to ensure it includes the new family
      // This ensures the family appears in the list even if there's a timing issue
      await fetchFamilies();
      
      // Set the newly created family as current
      setCurrentFamily(newFamily);
      localStorage.setItem('currentFamilyId', newFamily.id);
      
      return newFamily;
    } catch (error) {
      throw error;
    }
  };

  const joinFamily = async (inviteToken) => {
    try {
      const response = await api.post(`/families/join/${inviteToken}/`);
      await fetchFamilies(); // Refresh families list
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const value = {
    families,
    currentFamily,
    loading,
    selectFamily,
    createFamily,
    joinFamily,
    refreshFamilies: fetchFamilies,
  };

  return <FamilyContext.Provider value={value}>{children}</FamilyContext.Provider>;
};
